import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import type { ItemDto, ItemUnit, OrderDto, OrderSavedDto } from '@tiffinify/contracts';
import { addDays, formatDay, formatPhone, mealForTime, mealsOf, orderLineText, waLink, isDeliveryDay, type Meal } from '@tiffinify/core';
import { Icon, LoadError, PageLoader, useConfirm } from '../components/ui';
import { Amt, Av, Back, Badge, Btn, Btn2, Card, Chip, Chips, Empty, Ends, Field, G2, Group, Ic, Kick, Mk, Note, Row, Screen, Seg, Sheet, SheetHead, Sm, Sp, Title, Top } from '../components/p';
import { ScheduleField } from '../components/ScheduleField';
import { del, post, put } from '../lib/api';
import { errorMessage, formatInr, mealName } from '../lib/format';
import { useItems, useLastOrder, useMe, useOrder, useOrders, useSettings, useSubscribers } from '../lib/queries';

const PAY: Record<OrderDto['payState'], { label: string; tone: '' | 's' | 'g' | 'r' }> = {
  unpaid: { label: 'Unpaid', tone: 'r' }, says_paid: { label: 'Says paid', tone: 's' }, paid: { label: 'Paid', tone: 'g' }, refunded: { label: 'Refunded', tone: '' },
};
const STATUS: Record<OrderDto['status'], { label: string; tone: '' | 's' | 'g' | 'r' }> = {
  waiting: { label: 'Waiting', tone: 's' }, confirmed: { label: 'Confirmed', tone: 'g' }, cancelled: { label: 'Cancelled', tone: 'r' }, delivered: { label: 'Delivered', tone: '' },
};
const linesText = (lines: OrderDto['lines']) => lines.map(orderLineText).join(' · ');
const mealsOfOrder = (o: OrderDto) => o.lines.reduce((n, l) => n + (l.unit === 'kg' ? 0 : l.qty), 0);
/** A party: 15 meals or more in one order. */
const isParty = (o: OrderDto) => mealsOfOrder(o) >= 15;
const weekday = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short' });
const dayWord = (iso: string, today: string, tomorrow?: string) => (iso === today ? 'Today' : iso === tomorrow ? 'Tomorrow' : `${weekday(iso)} ${Number(iso.slice(8))}`);
const qtyText = (unit: ItemUnit, qty: number) => (unit === 'kg' ? orderLineText({ name: '', unit, qty }).trim() : String(qty));

/** Orders by day (P05), the Menus tab's first view: a date strip, each meal's count, filters, and the orders. */
export function OrdersView() {
  const [params, setParams] = useSearchParams();
  const q = useOrders(params.get('day') ?? undefined);
  const d = q.data;
  const [filter, setFilter] = useState<'all' | 'waiting' | 'unpaid' | 'party'>('all');
  if (q.isError && !d) return <LoadError what="the orders" onRetry={() => void q.refetch()} />;
  if (!d) return <PageLoader label="Loading the orders" />;
  const live = d.orders.filter((o) => o.status !== 'cancelled');
  const counts = {
    all: d.orders.length,
    waiting: live.filter((o) => o.status === 'waiting').length,
    unpaid: live.filter((o) => o.payState === 'unpaid' || o.payState === 'says_paid').length,
    party: live.filter(isParty).length,
  };
  const shown = d.orders.filter((o) => filter === 'all' || (o.status !== 'cancelled' && (
    filter === 'waiting' ? o.status === 'waiting' : filter === 'unpaid' ? o.payState === 'unpaid' || o.payState === 'says_paid' : isParty(o))));
  const pick = (date: string) => setParams((p) => { const n = new URLSearchParams(p); if (date === d.today) n.delete('day'); else n.set('day', date); return n; }, { replace: true });

  return (
    <>
      <div className="p-ds p-days-scroll" role="group" aria-label="Pick a day">
        {d.days.map((x) => (
          <button key={x.date} type="button" aria-pressed={x.date === d.day}
            className={x.date === d.day ? 'on' : !x.open ? 'x' : x.count > 0 ? 'busy' : undefined} onClick={() => pick(x.date)}>
            {x.date === d.today ? 'Today' : dayWord(x.date, d.today)}<b>{x.open ? x.count : '–'}</b>
          </button>
        ))}
      </div>
      <Card pad style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, d.meals.length)}, minmax(0, 1fr))`, padding: '14px 0' }}>
        {d.meals.map((m, i) => (
          <div key={m.meal} style={{ padding: '0 16px', borderLeft: i ? '1px solid #F2EDE6' : undefined }}>
            <Kick>{mealName(m.meal)}</Kick>
            <div className="p-mid" style={{ fontSize: 24, margin: '6px 0 2px' }}>{m.meals} {m.meals === 1 ? 'meal' : 'meals'}</div>
            <small style={{ fontSize: 12.5, color: '#6A6359' }}>{m.orders} {m.orders === 1 ? 'order' : 'orders'}</small>
          </div>
        ))}
      </Card>
      <Chips label="Filter orders">
        {(['all', 'waiting', 'unpaid', 'party'] as const).map((f) => (
          <Chip key={f} on={filter === f} onClick={() => setFilter(f)}>{f === 'all' ? 'All' : f === 'waiting' ? 'Waiting' : f === 'unpaid' ? 'Unpaid' : 'Party'} {counts[f]}</Chip>
        ))}
      </Chips>
      {shown.length === 0 ? (
        <Card><Empty title={`No orders ${d.day === d.today ? 'today' : `on ${formatDay(d.day)}`}`}>Add one with ＋, or share your link.</Empty></Card>
      ) : (
        <Card>
          {shown.map((o) => (
            <Row key={o.id} to={`/orders/${o.id}`} style={o.status === 'cancelled' ? { opacity: 0.5 } : undefined} left={<Av name={o.name} />}
              t={`${o.name} · #${o.number}`}
              s={[mealName(o.meal), linesText(o.lines), o.source === 'link' ? 'booked by link' : 'added by you', o.onPlan ? 'extra on a plan' : null].filter(Boolean).join(' · ')}
              right={<Ends amt={formatInr(o.total)} badge={o.status === 'cancelled' ? <Badge>Cancelled</Badge> : <Badge tone={PAY[o.payState].tone}>{PAY[o.payState].label}</Badge>} />} />
          ))}
        </Card>
      )}
    </>
  );
}

type Person = { id?: string; name: string; phone: string; area: string };
type Picked = { item: Pick<ItemDto, 'id' | 'name' | 'unit' | 'price' | 'stepG' | 'minG' | 'food'>; qty: number };

/** New order (P06) by hand: who, when, what, and paid now or later. With an id, it changes that order. */
export function NewOrder() {
  const { id = '' } = useParams();
  const editing = !!id;
  const nav = useNavigate();
  const qc = useQueryClient();
  const me = useMe().data;
  const served = me ? mealsOf(me.kitchen.meals) : [];
  const strip = useOrders(undefined).data;
  const settings = useSettings().data;
  const items = useItems().data?.items ?? [];
  const orderQuery = useOrder(id);
  const existing = orderQuery.data?.order;

  const initialCustomer = useLocation().state?.customer as Person | undefined;
  const [person, setPerson] = useState<Person | null>(() => editing ? null : initialCustomer ?? null);
  const [q, setQ] = useState('');
  const [fresh, setFresh] = useState<Person>({ name: '', phone: '', area: '' });
  const [day, setDay] = useState<string>('');
  const [meal, setMeal] = useState<Meal | undefined>(undefined);
  const [picked, setPicked] = useState<Picked[]>([]);
  const [paid, setPaid] = useState<'later' | 'cash' | 'upi'>('later');
  const [note, setNote] = useState('');
  const [adding, setAdding] = useState(false);
  const [newItem, setNewItem] = useState<{ name: string; price: string; unit: ItemUnit }>({ name: '', price: '', unit: 'plate' });
  const found = useSubscribers(q.trim(), 'all').data?.items ?? [];
  const last = useLastOrder(!editing ? person?.id : undefined).data?.order;

  // Changing an order starts from what it has.
  useEffect(() => {
    if (!existing || person) return;
    setPerson({ id: existing.subscriberId, name: existing.name, phone: existing.phone, area: existing.area });
    setDay(existing.day); setMeal(existing.meal); setNote(existing.note);
    setPicked(existing.lines.map((l) => ({ item: { id: l.itemId ?? `line:${l.id}`, name: l.name, unit: l.unit, price: l.price, stepG: 250, minG: 250, food: 'veg' }, qty: l.qty })));
  }, [existing, person]);

  const today = strip?.today ?? me?.today ?? '';
  const open = (strip?.days ?? []).filter((x) => x.open).slice(0, 3);
  const when = day || open[0]?.date || today;
  const mealNow = meal ?? (when === today ? mealForTime(served) : served[0]) ?? 'lunch';
  const lineAmount = (p: Picked) => (p.item.unit === 'kg' ? Math.round((p.item.price * p.qty) / 1000) : p.item.price * p.qty);
  const total = picked.reduce((n, p) => n + lineAmount(p), existing?.deliveryCharge ?? 0);
  const step = (it: Picked['item']) => (it.unit === 'kg' ? it.stepG : 1);
  const setQty = (it: Picked['item'], qty: number) => setPicked((list) => {
    const rest = list.filter((p) => p.item.id !== it.id);
    if (qty <= 0) return rest;
    return [...rest, { item: it, qty }];
  });
  const qtyOf = (itemId: string) => picked.find((p) => p.item.id === itemId)?.qty ?? 0;
  const useLast = () => {
    if (!last) return;
    setMeal(last.meal);
    setPicked(last.lines.map((l) => ({ item: items.find((i) => i.id === l.itemId) ?? { id: l.itemId ?? `line:${l.id}`, name: l.name, unit: l.unit, price: l.price, stepG: 250, minG: 250, food: 'veg' }, qty: l.qty })));
  };

  const addItem = useMutation({
    mutationFn: () => post<ItemDto>('/items', { name: newItem.name, unit: newItem.unit, price: Number(newItem.price) || 0 }),
    onSuccess: (it) => { void qc.invalidateQueries({ queryKey: ['items'] }); setQty(it, it.unit === 'kg' ? it.minG : 1); setAdding(false); setNewItem({ name: '', price: '', unit: 'plate' }); },
  });
  const save = useMutation({
    mutationFn: () => {
      const lines = picked.map((p) => ({ itemId: p.item.id.startsWith('line:') ? null : p.item.id, name: p.item.name, unit: p.item.unit, qty: p.qty, price: p.item.price }));
      if (editing) return put<OrderSavedDto>(`/orders/${id}`, { day: when, meal: mealNow, lines, note });
      return post<OrderSavedDto>('/orders', {
        ...(person?.id ? { subscriberId: person.id } : { customer: { name: person?.name ?? '', phone: person?.phone ?? '', area: person?.area ?? '' } }),
        day: when, meal: mealNow, lines, paid, note,
      });
    },
    onSuccess: (res) => { void qc.invalidateQueries(); nav(`/orders/${res.order.id}`, { replace: editing, state: { message: res.message, saved: editing ? 'Changes saved.' : `Order #${res.order.number} saved.` } }); },
  });

  if (editing && orderQuery.isError) {
    return <Screen><Top><Back to="/menus" label="Orders" /></Top><LoadError what="this order" onRetry={() => void orderQuery.refetch()} /></Screen>;
  }
  if (editing && (!existing || !person)) {
    return <Screen><Top><Back to="/menus" label="Orders" /></Top><PageLoader label="Opening the order" /></Screen>;
  }

  return (
    <Screen gap={16}>
      <Top>
        <Back to={editing ? `/orders/${id}` : '/menus'} label={editing ? 'Order' : 'Cancel'} /><Sp />
        {!editing && <span className="p-sub" style={{ fontSize: 13 }}>Paste a message · coming soon</span>}
      </Top>
      {existing?.payState === 'paid' && <Note>Moving this paid order keeps its payment. To change the total, first remove its payment mark from the order details.</Note>}
      <Title h={editing ? `Change order${existing ? ` #${existing.number}` : ''}` : 'New order'} />

      {person ? (
        <Card>
          <Row left={<Av name={person.name} />} t={person.name} s={[person.phone, person.area].filter(Boolean).join(' · ')}
            right={!editing ? <Sm label="Change" onClick={() => { setPerson(null); setPicked([]); }} /> : undefined} />
          {last && <Row left={<Ic icon="refresh" tone="s" />} t="Repeat last order" s={`${linesText(last.lines)} · ${mealName(last.meal).toLowerCase()}`} right={<Sm label="Use" k onClick={useLast} />} />}
        </Card>
      ) : (
        <Group label="Who is it for?">
          <label className="p-search" style={{ boxShadow: 'inset 0 0 0 1px #ECE5DA' }}>
            <Icon name="search" size={18} sw={2} />
            <input aria-label="Find a customer" value={q} placeholder="Name, phone or flat" onChange={(e) => setQ(e.target.value)} />
          </label>
          {q.trim() && found.length > 0 && (
            <Card>
              {found.slice(0, 5).map((s) => (
                <Row key={s.subscriberId} onClick={() => setPerson({ id: s.subscriberId, name: s.name, phone: s.phone, area: s.area })} left={<Av name={s.name} />} t={s.name} s={[s.phone, s.area].filter(Boolean).join(' · ')} />
              ))}
            </Card>
          )}
          <Kick>Or someone new</Kick>
          <G2>
            <Field label="Name" id="o-name" value={fresh.name} onChange={(v) => setFresh({ ...fresh, name: v })} />
            <Field label="WhatsApp number" id="o-phone" value={fresh.phone} onChange={(v) => setFresh({ ...fresh, phone: v })} inputMode="tel" />
          </G2>
          <Field label="Area, block or flat" id="o-area" value={fresh.area} onChange={(v) => setFresh({ ...fresh, area: v })} />
          <Sm label="Use this customer" k disabled={!fresh.name.trim() || fresh.phone.replace(/\D/g, '').length < 10} onClick={() => setPerson({ ...fresh })} />
        </Group>
      )}

      <Group label="When">
        <Chips label="Day">
          {open.map((x) => <Chip key={x.date} on={when === x.date} onClick={() => setDay(x.date)}>{dayWord(x.date, today, addDays(today, 1))}</Chip>)}
        </Chips>
        <ScheduleField label="Pick a delivery date" id="o-day" value={when} today={today} min={today} max={today ? addDays(today, 60) : undefined}
          disabledFor={date => settings ? !isDeliveryDay(date, { deliveryDays: settings.kitchen.deliveryDays, holidays: settings.holidays.map(holiday => holiday.day) }) : false}
          onChange={setDay} />
        {served.length > 1 && <Seg label="Meal" value={mealNow} onChange={(m) => setMeal(m)} options={served.map((m) => ({ value: m, label: mealName(m) }))} />}
      </Group>

      <Group label="Items" right={<button type="button" className="p-link" onClick={() => setAdding(!adding)}>{adding ? 'Close' : '+ Add an item'}</button>}>
        {items.length === 0 && !adding && <Note>Add what you sell one-off, like a Veg thali or a sweet by weight. It stays for next time.</Note>}
        {(items.length > 0 || picked.length > 0) && (
          <Card>
            {[...items, ...picked.map((p) => p.item).filter((it) => !items.some((i) => i.id === it.id))].map((it) => {
              const n = qtyOf(it.id);
              const s = step(it);
              return (
                <Row key={it.id} left={<Mk food={it.food} />} t={it.name} s={it.unit === 'kg' ? `${formatInr(it.price)} / kg` : `${formatInr(it.price)} ${it.unit === 'plate' ? '' : `a ${it.unit}`}`.trim()}
                  right={n > 0 ? (
                    <span className="p-step">
                      <button type="button" aria-label={`Less ${it.name}`} onClick={() => setQty(it, n - s < (it.unit === 'kg' ? it.minG : 1) ? 0 : n - s)}>–</button>
                      <b>{qtyText(it.unit, n)}</b>
                      <button type="button" className="k" aria-label={`More ${it.name}`} onClick={() => setQty(it, n + s)}>+</button>
                    </span>
                  ) : <Sm label="Add" onClick={() => setQty(it, it.unit === 'kg' ? it.minG : 1)} />} />
              );
            })}
          </Card>
        )}
        {adding && (
          <Card pad style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <G2>
              <Field label="Item" id="i-name" value={newItem.name} placeholder="Veg thali" onChange={(v) => setNewItem({ ...newItem, name: v })} />
              <Field label={newItem.unit === 'kg' ? 'Price per kg' : 'Price'} id="i-price" value={newItem.price} inputMode="numeric" placeholder="₹" onChange={(v) => setNewItem({ ...newItem, price: v.replace(/\D/g, '') })} />
            </G2>
            <Seg label="Sold as" value={newItem.unit} onChange={(u) => setNewItem({ ...newItem, unit: u })}
              options={[{ value: 'plate', label: 'Plate' }, { value: 'pack', label: 'Pack' }, { value: 'box', label: 'Box' }, { value: 'kg', label: 'By weight' }]} />
            {addItem.error && <span className="p-error" role="alert">{errorMessage(addItem.error)}</span>}
            <Btn2 label={addItem.isPending ? 'Saving…' : 'Save item'} icon="check" disabled={!newItem.name.trim() || addItem.isPending} onClick={() => addItem.mutate()} />
          </Card>
        )}
      </Group>

      {!editing && <Seg label="Payment" value={paid} onChange={setPaid} options={[{ value: 'later', label: 'Pay later' }, { value: 'cash', label: 'Paid cash' }, { value: 'upi', label: 'Paid UPI' }]} />}
      <Field label="Note (optional)" id="o-note" value={note} placeholder="Less spicy" onChange={setNote} />
      {save.error && <span className="p-error" role="alert">{errorMessage(save.error)}</span>}
      <Btn label={editing ? `Save changes · ${formatInr(total)}` : `Save ${formatInr(total)} · send on WhatsApp`} go="send"
        disabled={!person || picked.length === 0} busy={save.isPending} onClick={() => save.mutate()} />
    </Screen>
  );
}

/** Order details (P07): status, the customer, what is in it, payment, and every change. */
export function OrderDetails() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const told = (useLocation().state ?? {}) as { message?: { link: string }; saved?: string };
  const q = useOrder(id);
  const { confirm, sheet } = useConfirm();
  const done = () => void qc.invalidateQueries();
  const [labelOpen, setLabelOpen] = useState(false);
  const customerLink = useMutation({
    mutationFn: (rotate: boolean) => post<{path: string}>(`/orders/${id}/customer-link${rotate ? '/reset' : ''}`),
  });
  const pay = useMutation({ mutationFn: (method: 'upi' | 'cash') => put(`/orders/${id}/paid`, { method }), onSuccess: done });
  const unpay = useMutation({ mutationFn: () => del(`/orders/${id}/paid`), onSuccess: done });
  const accept = useMutation({ mutationFn: () => post(`/orders/${id}/confirm`), onSuccess: done });
  const cancel = useMutation({ mutationFn: () => post(`/orders/${id}/cancel`), onSuccess: done });

  if (q.isError && !q.data) return <Screen><Top><Back to="/menus" label="Orders" /></Top><LoadError what="this order" onRetry={() => void q.refetch()} /></Screen>;
  if (!q.data) return <Screen><Top><Back to="/menus" label="Orders" /></Top><PageLoader label="Opening the order" /></Screen>;
  const { order: o, message } = q.data;
  const live = o.status === 'waiting' || o.status === 'confirmed';
  const err = pay.error ?? unpay.error ?? cancel.error ?? accept.error ?? customerLink.error;

  if (o.status === 'waiting') {
    return (
      <Sheet label="Late order" onClose={() => nav('/menus')}>
        <SheetHead kick="After the kitchen’s cutoff" title="Late order" onClose={() => nav('/menus')} />
        <Card><Row left={<Av name={o.name} />} t={o.name}
          s={[`#${o.number} · ${formatDay(o.day)} · ${mealName(o.meal)}`, o.area, linesText(o.lines)].filter(Boolean).join(' · ')}
          right={<Amt>{formatInr(o.total)}</Amt>} /></Card>
        <Note icon="clock">This request is waiting. Accept it to add it to the cook, pack and delivery lists.</Note>
        {err && <span className="p-error" role="alert">{errorMessage(err)}</span>}
        <G2>
          <Btn2 label="Say no" disabled={cancel.isPending || accept.isPending} onClick={() => cancel.mutate()} />
          <Btn label="Accept order" busy={accept.isPending} disabled={cancel.isPending} go="check" onClick={() => accept.mutate()} />
        </G2>
      </Sheet>
    );
  }

  return (
    <Screen gap={16}>
      <Top><Back to={`/menus?day=${o.day}`} label="Orders" /><Sp /></Top>
      {told.saved && <Note icon="check">{told.saved} {told.message && <a href={told.message.link} target="_blank" rel="noreferrer">Send the confirmation on WhatsApp</a>}</Note>}
      <Title h={`Order #${o.number}`} badge={<Badge tone={STATUS[o.status].tone}>{STATUS[o.status].label}</Badge>}
        sub={`${formatDay(o.day)} · ${mealName(o.meal).toLowerCase()} · ${o.source === 'link' ? 'booked by link' : 'added by you'}${o.onPlan ? ' · extra on a plan' : ''}`} />
      <Card>
        <Row left={<Av name={o.name} />} t={<Link to={`/s/${o.subscriberId}`}>{o.name}</Link>} s={[o.area, formatPhone(o.phone)].filter(Boolean).join(' · ')}
          right={<span className="p-pair">
            <a className="p-ib" href={`tel:${o.phone}`} aria-label={`Call ${o.name}`}><Icon name="phone" size={17} sw={1.9} /></a>
            <a className="p-ib" href={message.link.split('?')[0]} target="_blank" rel="noreferrer" aria-label={`WhatsApp ${o.name}`}><Icon name="chat" size={17} sw={1.9} /></a>
          </span>} />
      </Card>
      {o.status !== 'cancelled' && (
        <Card pad>
          <div className="p-trk">
            <span className="on">Booked</span>
            <span className="on">Confirmed</span>
            <span className={o.status === 'delivered' ? 'on' : ''}>On the way</span>
            <span className={o.status === 'delivered' ? 'on' : ''}>Delivered</span>
          </div>
        </Card>
      )}
      <Card>
        {o.lines.map((l) => <Row key={l.id} t={orderLineText(l)} s={l.unit === 'kg' ? `${formatInr(l.price)} / kg` : `${formatInr(l.price)} each`} right={<Amt>{formatInr(l.amount)}</Amt>} />)}
        {o.menuId && <Row t="Delivery" right={<Amt>{o.deliveryCharge ? formatInr(o.deliveryCharge) : 'Free'}</Amt>} />}
        <Row t="Total" right={<Amt>{formatInr(o.total)}</Amt>} />
      </Card>
      {o.note && <Note>{o.note}</Note>}
      <Card pad style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="p-sec"><Kick>Payment</Kick><Badge tone={PAY[o.payState].tone}>{PAY[o.payState].label}{o.paidMethod ? ` · ${o.paidMethod === 'upi' ? 'UPI' : 'cash'}` : ''}</Badge></div>
        {o.payState === 'paid' ? (
          <div className="p-links"><button type="button" onClick={() => unpay.mutate()}>Not paid after all</button></div>
        ) : o.status !== 'cancelled' && (
          <div style={{ display: 'flex', gap: 10 }}>
            {o.payState === 'says_paid' && <Sm label="Not received" onClick={() => unpay.mutate()} />}
            <Sm label="Paid cash" onClick={() => pay.mutate('cash')} disabled={pay.isPending} />
            <Sm label="Paid UPI" k onClick={() => pay.mutate('upi')} disabled={pay.isPending} />
          </div>
        )}
      </Card>
      {live && (
        <nav className="p-qa" aria-label="Order actions" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
          <Link to={`/orders/${o.id}/edit`}><span><Icon name="swap" size={18} sw={1.9} /></span>Move day</Link>
          <Link to={`/orders/${o.id}/edit`}><span><Icon name="edit" size={18} sw={1.9} /></span>Change items</Link>
          <button type="button" onClick={() => void confirm({ title: `Cancel order #${o.number}?`, body: 'It comes off the kitchen list and the delivery run.', yes: 'Cancel order', danger: true }).then((ok) => { if (ok) cancel.mutate(); })}>
            <span><Icon name="close" size={18} sw={1.9} /></span>Cancel
          </button>
        </nav>
      )}
      {err && <span className="p-error" role="alert">{errorMessage(err)}</span>}
      {(o.status === 'confirmed' || o.status === 'delivered') &&
        <Btn2 label="Send the confirmation on WhatsApp" icon="send" href={message.link} />}
      <G2>
        <Btn2 label="Customer order link" icon="link" disabled={customerLink.isPending} onClick={() => customerLink.mutate(false)} />
        <Btn2 label="Print label" icon="sheet" onClick={() => setLabelOpen(true)} />
      </G2>
      {customerLink.data && <Sheet label="Customer order link" onClose={customerLink.reset}>
        <SheetHead title="Customer order link" onClose={customerLink.reset} />
        <Note>{o.name} can use this private link to check order #{o.number}.</Note>
        <Btn label="Share on WhatsApp" go="send" href={waLink(o.phone, `Your order #${o.number}: ${location.origin}${customerLink.data.path}`)} />
        <Btn2 label="Open customer page" to={customerLink.data.path} />
        <Btn2 label="Replace this link" icon="refresh" onClick={async () => {
          if (await confirm({title:'Replace this order link?', body:'The old link will stop opening this order.', yes:'Replace link', danger:true})) customerLink.mutate(true);
        }} />
      </Sheet>}
      {labelOpen && <Sheet label="Order label" onClose={() => setLabelOpen(false)}>
        <SheetHead title="Order label" onClose={() => setLabelOpen(false)} />
        <div className="p-menu-sticker"><Kick>Order #{o.number} · {formatDay(o.day)}</Kick><h2>{o.name}</h2>
          <strong>{o.area}</strong>{o.lines.map((line) => <span key={line.id}>{orderLineText(line)}</span>)}
          {o.note && <small>{o.note}</small>}
        </div>
        <Btn label="Print label" onClick={() => window.print()} />
      </Sheet>}
      {sheet}
    </Screen>
  );
}

/** One meal's orders inside Kitchen: what to cook (totals per item), what to pack (per order), and the drops to mark. */
export function OrdersForMeal({ day, meal, mode }: { day: string; meal: Meal; mode: 'cook' | 'pack' | 'deliver' }) {
  const qc = useQueryClient();
  const d = useOrders(day).data;
  const mark = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) => (on ? put(`/orders/${id}/delivered`) : del(`/orders/${id}/delivered`)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['orders'] }),
  });
  if (!d || d.day !== day) return null;
  const here = d.orders.filter((o) => o.meal === meal && (o.status === 'confirmed' || o.status === 'delivered'));
  if (here.length === 0) return null;

  if (mode === 'cook') {
    const totals = new Map<string, { name: string; unit: ItemUnit; qty: number }>();
    for (const l of here.flatMap((o) => o.lines)) {
      const key = `${l.name}|${l.unit}`;
      const t = totals.get(key) ?? { name: l.name, unit: l.unit, qty: 0 };
      t.qty += l.qty;
      totals.set(key, t);
    }
    return (
      <Group label={`Orders · ${here.length} ${here.length === 1 ? 'order' : 'orders'}`}>
        <Card>{[...totals.values()].map((t) => <Row key={`${t.name}|${t.unit}`} t={t.name} right={<Amt>{t.unit === 'kg' ? qtyText('kg', t.qty) : `×${t.qty}`}</Amt>} />)}</Card>
      </Group>
    );
  }
  if (mode === 'pack') {
    return (
      <Group label={`Orders to pack · ${here.length}`}>
        <Card>{here.map((o) => <Row key={o.id} to={`/orders/${o.id}`} left={<Av name={o.name} />} t={`${o.name} · #${o.number}`} s={[linesText(o.lines), o.note].filter(Boolean).join(' · ')} right={<Badge>{mealsOfOrder(o) || '—'}</Badge>} />)}</Card>
      </Group>
    );
  }
  const done = here.filter((o) => o.status === 'delivered').length;
  return (
    <Group label={`Orders · ${done} of ${here.length} delivered`}>
      <Card>
        {here.map((o) => {
          const on = o.status === 'delivered';
          const collect = o.payState === 'paid' ? null : `collect ${formatInr(o.total)}`;
          return (
            <Row key={o.id} t={`${o.name} · #${o.number}`} s={[o.area, linesText(o.lines), collect].filter(Boolean).join(' · ')}
              right={<button type="button" className={on ? 'p-tick on' : 'p-tick'} aria-label={on ? `${o.name} delivered, tap to undo` : `Mark ${o.name} delivered`} aria-pressed={on}
                disabled={mark.isPending} onClick={() => mark.mutate({ id: o.id, on: !on })}><Icon name="check" size={20} sw={2.6} /></button>} />
          );
        })}
      </Card>
    </Group>
  );
}
